import React, { createContext, useCallback, useContext, useState } from 'react';
import { QuickConsultInfoFromUntact } from '@remodeling/remodeling-manager';
import { LayerPopUp, useLayerPopUp } from '@/providers/LayerPopUpProvider';
import QuickConsult from '@/feat-components/QuickConsult';
import { format } from 'date-fns';
import { useBraze } from '@/providers/BrazeProvider';

export interface QuickConsultOptionsType {
  channelType: string | string[];
  channelId?: string | string[] | undefined;
  channelUrl?: string | string[] | undefined;
  agentEmployeeNo?: string | string[] | undefined;
  untactInfo?: QuickConsultInfoFromUntact;
}

interface QuickConsultContextType {
  handleOpen: (options: QuickConsultOptionsType) => void;
}

const QuickConsultContext = createContext<QuickConsultContextType>({
  handleOpen: () => {},
});

export function useQuickConsultContext() {
  const context = useContext(QuickConsultContext);
  if (context === undefined) {
    throw new Error(
      'useQuickConsult must be used within a QuickConsultContext',
    );
  }
  return context;
}

function QuickConsultProvider({ children }: { children?: React.ReactNode }) {
  const [quickConsultOptions, setQuickConsultOptions] =
    useState<QuickConsultOptionsType | null>();
  const { handleOpen: handleLayerPopUpOpen } = useLayerPopUp();
  const { logCustomEvent } = useBraze();

  const handleOpen = useCallback(
    (options: QuickConsultOptionsType) => {
      setQuickConsultOptions({
        ...options,
      });
      handleLayerPopUpOpen('QuickConsultLayerPopUp');
      logCustomEvent({
        eventName: 'bz_start_fastconsult',
        eventProperties: {
          bz_date: format(new Date(), 'yyyy-MM-dd'),
          bz_consultType: 'quick',
        },
      });
    },
    [handleLayerPopUpOpen, logCustomEvent],
  );

  return (
    <QuickConsultContext.Provider
      value={{
        handleOpen,
      }}
    >
      {children}
      <QuickConsultContext.Consumer>
        {(value) => (
          <LayerPopUp id="QuickConsultLayerPopUp" depth="MAX" direction="up">
            {({ handleClose: handleCloseLayerPopUp }) => (
              <QuickConsult
                {...value}
                {...quickConsultOptions}
                handleCloseLayerPopUp={handleCloseLayerPopUp}
              />
            )}
          </LayerPopUp>
        )}
      </QuickConsultContext.Consumer>
    </QuickConsultContext.Provider>
  );
}

export default QuickConsultProvider;
